import GlobalLoader from "../../../components/Common/GlobalLoader";
import DeleteModal from "../../../components/Global/DeleteModal";
import BreadCrumb from "../../../components/Common/BreadCrumb";
import SinpleActionButton from "../../../components/Global/SinpleActionButton";

import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {
  addJournalEntry,
  getJournalEntries,
  getSubClassAccount,
  updateJournalEntry
} from "../../../helpers/backend_helper";
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Pagination,
  PaginationItem,
  PaginationLink,
  Table
} from "reactstrap";
import "./index.css";
import TableContainer from "../../../components/Common/TableContainer";
import * as XLSX from 'xlsx'
import moment from 'moment'


export const Journal = (props) => {
  const {t} = useTranslation();
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [journalEntries, setJournalEntries] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [subClassAccounts, setSubClassAccount] = useState([]);
  const [totalDebitAmount, setTotalDebitAmount] = useState(0);
  const [totalCreditAmount, setTotalCreditAmount] = useState(0);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]); // Set default date to today
  const [note, setNote] = useState("");
  const [selectJournal, setSelectJournal] = useState({});

  const [journalEntryLines, setJournalEntryLines] = useState([
    {
      "id": 1,
      "classAccountId": "",
      "debit": {
        "amount": 0,
      },
      "description": "" //
    },
    {
      "id": 2,
      "classAccountId": "",
      "credit": {
        "amount": 0
      },
      "description": "" //
    }
  ]);
  const toggle = () => {
    if (showModal){
      setShowModal(false);
      setSelectJournal({});
      setNote("")
      setDate(new Date().toISOString().split('T')[0]);
      setJournalEntryLines([
        {
          "id": 1,
          "classAccountId": "",
          "debit": {
            "amount": 0,
          },
          "description": "" //
        },
        {
          "id": 2,
          "classAccountId": "",
          "credit": {
            "amount": 0
          },
          "description": "" //
        }
      ]);
      setIsEdit(false);
    }else {
      setShowModal(true);
    }

  };


 const getJournal = useCallback(async () => {
    setLoading(true);
    try {
      const journals = await getJournalEntries({page: page, size: size});
      
      setJournalEntries(journals?.data);
      setLoading(false);
    }catch (e) {
      setLoading(false);
    }
  }, [page, size]);

  const getSubAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const subAccounts = await getSubClassAccount();
      const formatted = subAccounts.data.map((subAccount) => ({ label: subAccount.name, value: subAccount.id}));
      setSubClassAccount(formatted);
    }catch (e) {
      setLoading(false);
    }
  }, [])

  const addNewJournal = useCallback( async (data) => {
    setLoading(true);
    try {
      const journal = await addJournalEntry(data);
      setLoading(false);
      getJournal();
    } catch (e) {
      console.error(e)
      setLoading(false);
    }
  }, [journalEntries]);

  const updateJournal = useCallback( async (data) => {
    setLoading(true);
    try {
      const journal = await updateJournalEntry(data);
      setLoading(false);
      getJournal();
    } catch (e) {
      console.error(e)
      setLoading(false);
    }
  }, [journalEntries])

  const addNewLine = () => {
    setJournalEntryLines([
      ...journalEntryLines,
      {
        "id": journalEntryLines.length + 1,
        "classAccountId": "",
        "credit": {
          "amount": 0
        },
        "debit": {
          "amount": 0,
        },
        "description": "" //
      }
    ]);
  }
  const removeLine = (index) => {
    const newLines = journalEntryLines.filter((line, i) => i !== index);
    setJournalEntryLines(newLines);
  }
  const totalDebit = () => {
    const sumDebit =  journalEntryLines.reduce((total, line) => {
      return total + (line.debit ? Number(line.debit.amount) : 0);
    }, 0);
    setTotalDebitAmount(sumDebit);
  };
  const totalCredit = () => {
    const sumCredit = journalEntryLines.reduce((total, line) => {
      return total + (line.credit ? Number(line.credit.amount) : 0);
    }, 0);
    setTotalCreditAmount(sumCredit);
  }

  const handleDebitChange = (index, value) => {
    const updatedLines = journalEntryLines.map((line, i) => (
      (i === index) ? {...line, debit: {amount: value}, credit: {amount: 0}} : line
    ));
    setJournalEntryLines(updatedLines);
  }

  const handleCreditChange = (index, value) => {
    const updatedLines = journalEntryLines.map((line, i) => (
      (i === index) ? {...line, credit: {amount: value}, debit: {amount: 0}} : line
    ));
    setJournalEntryLines(updatedLines);
  }

  const handleDescriptionChange = (index, value) => {
    const updatedLines = journalEntryLines.map((line, i) => (
      (i === index) ? {...line, description: value} : line
    ));
    setJournalEntryLines(updatedLines);
  }

  const handleAccountChange = (index, value) => {
    const updatedLines = journalEntryLines.map((line, i) => (
      (i === index) ? {...line, classAccountId: value} : line
    ));
    setJournalEntryLines(updatedLines);
  }

  const concatClassAccounts = (journalEntry) => {
    const classAccounts = journalEntry.journalEntryLines.map((line) => line.classAccountId);
    const filteredAccounts = subClassAccounts.filter(account => classAccounts.includes(account.value));
    const accountLabels = filteredAccounts.map(account => account.label);
    return accountLabels.join(", ");
  };


  const columns = useMemo(
    () => [
      {
        header: t("date"),
        accessorKey: "date",
        style: "button",
        enableColumnFilter: false,
      },
      {
        header: t("journal_ref"),
        accessorKey: "journalReference",
        style: "button",
        enableColumnFilter: false,
      },
      {
        header: t("notes"),
        accessorKey: "notes",
        style: "button",
        enableColumnFilter: false,
        cell: (cellProps) => {
          return (
            <span className="text-truncate">
              {cellProps.row.original.notes ? cellProps.row.original.notes : concatClassAccounts(cellProps.row.original)}
            </span>
          );
        }
      },
      {
        header: t("amount"),
        accessorKey: "amount",
        style: "button",
        enableColumnFilter: false,
      },
      {
        header: t("action"),
        cell: ( cellProps ) => {
          return (
            <button className="btn" onClick={() => handleEdit(cellProps.row.original)}>
              <img src="../assets/images/edit.png" alt="" className="me-2"></img>
              <span>{t('edit')}</span>
            </button>
          )
        }
      }
    ],
    []
  );

  useEffect(() => {
    totalDebit();
    totalCredit();
  }, [journalEntryLines]);
  useEffect(() => {
    getJournal();
    getSubAccounts();
  }, [page]);


  const handleAddJournalEntry = async () => {
    setLoading(true);
    const payload = {
      date: date,
      notes: note,
      journalEntryLines: journalEntryLines.map(line => ({
        classAccountId: line.classAccountId,
        entryType: line.debit.amount ? "DEBIT" : "CREDIT",
        amount: line.debit.amount ? Number(line.debit.amount) : Number(line.credit.amount),
        description: line.description
      }))
    };
    try {
      setShowModal(false);
      setLoading(false);
      if(totalCreditAmount !== totalDebitAmount) {
        alert(t("debit_credit_mismatch"));
        return;
      }
      if (!payload.date){
        alert(t("date_required"));
        return;
      }
      if (isEdit) {
        payload.id = selectJournal.id;
        setIsEdit(false);
        setSelectJournal({});
        await updateJournal(payload);
        toggle()
      } else {
        addNewJournal(payload);
      }
    } catch (error) {
      console.error("Error adding journal entry:", error);
      setLoading(false);
    }
  }

  const handleEdit = (selectJournal) => {
    console.log("selectJournal", selectJournal);
    setSelectJournal(selectJournal);
    setShowModal(true);
    setIsEdit(true);
    setNote(selectJournal.notes)
    setDate(selectJournal.date);
    const lines = selectJournal.journalEntryLines.map((line, index) => ({
      id: index + 1,
      classAccountId: line.classAccountId,
      debit: line.entryType === "DEBIT" ? { amount: line.amount } : { amount: 0 },
      credit: line.entryType === "CREDIT" ? { amount: line.amount } : { amount: 0 },
      description: line.description || ""
    }));
    setJournalEntryLines(lines);
  }

const handleExport = () => {
  if (!journalEntries?.items?.length) return;

  const worksheetData = journalEntries.items.map(entry => ({
    Date: entry.date,
    Reference: entry.journalReference,
    Notes: entry.notes,
    Amount: entry.amount,
  }));

  const worksheet = XLSX.utils.json_to_sheet(worksheetData);

  // Définir les largeurs de colonnes
  worksheet["!cols"] = [
    { wch: 15 }, // Date
    { wch: 20 }, // Reference
    { wch: 40 }, // Notes
    { wch: 15 }, // Amount
  ];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Journal");

  const filename = `journal_${moment().format("YYYYMMDD_HHmmss")}.xlsx`;
  XLSX.writeFile(workbook, filename);
};



  return(
    <>
      {
        loading ? <GlobalLoader />
          :
          <>
            <React.Fragment>
              <>
                <div className="all-beds-setting px-3">
                  <div className="patients-list py-4 px-2  ">
                    <div className="container-fluid">
                      <div className="row list-patients d-flex  justify-content-between">
                        <div className="col-lg-5">
                          <BreadCrumb title={t("journal")} pageTitle={t("accounting")} />
                        </div>
                        <div className="col-sm-auto ">
                           {/* rajout du bouton pour exporter */}
                           <button className="btn btn-success d-inline-flex align-items-center" onClick={handleExport} >
                                <i className="fa fa-file-excel me-1" /> {t("export_excell")}
                            </button>
                          <SinpleActionButton title={t("new_journal")} img="../assets/images/user.png" action={()=>(setShowModal(true))} />
                        </div>
                      </div>
                      <div>
                        <TableContainer
                          columns={columns}
                          data={journalEntries?.items || []}
                          isGlobalFilter={false}
                          isAddUserList={false}
                          customPageSize={100}
                          divClass="card table-card table-responsive p-0"
                          tableClass="table align-middle"
                          theadClass="table-light text-muted"
                          isCustomerFilter={true}
                          tablePage={journalEntries?.page || 0}
                          tableTotalPage={journalEntries?.total_page || 0}
                          tableTotal={journalEntries?.total_items}
                          showPagination={true}
                          onNextPage={(page) => setPage(page)}
                          SearchPlaceholder={t("search_journal")}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </>
            </React.Fragment>
          </>
      }
      <>
        <Modal
          isOpen={showModal}
          toggle={toggle}
          size="xl"
          fullscreen="xl"
        >
          <ModalHeader toggle={toggle}>
            {isEdit ? t("edit_journal") : t("new_journal")}
          </ModalHeader>
          <ModalBody>
            <form>
              <div className="row mb-3">
                <div className="col-md-4 col-12">
                  <label htmlFor="date">{t('date')}</label>
                  <input type="date" value={date} onChange={(e)=>setDate(e.target.value)} className="form-control" id="date" />
                </div>
              </div>
              <div>
                <Table bordered>
                  <thead>
                  <tr>
                    <th>
                      #
                    </th>
                    <th>
                      {t('account')}
                    </th>
                    <th>
                      {t('debit')}
                    </th>
                    <th>
                      {t('credit')}
                    </th>
                    <th>
                      {t('description')}
                    </th>
                    <th>
                    </th>
                  </tr>
                  </thead>
                  <tbody>
                  {
                    journalEntryLines.map((line, index) => (
                      <tr key={index}>
                        <th scope="row">
                          {line.id}
                        </th>
                        <td>
                          <select className="table-input" value={line.classAccountId} onChange={(e)=>handleAccountChange(index, e.target.value)} >
                            <option value="">{t('select_account')}</option>
                            {
                              subClassAccounts.map((account) => (
                                <option key={account.value} value={account.value}>
                                  {account.label}
                                </option>
                              ))
                            }
                          </select>
                        </td>
                        <td>
                          <input type="number" min={0} className="table-input" value={line?.debit?.amount} onChange={(e) => handleDebitChange(index, e.target.value)} />
                        </td>
                        <td>
                          <input type="number" min={0} className="table-input" value={line?.credit?.amount} onChange={(e) => handleCreditChange(index, e.target.value)} />
                        </td>
                        <td>
                          <input type="text" className="table-input" value={line.desctiption} onChange={(e) => handleDescriptionChange(index, e.target.value)} />
                        </td>
                        <td>
                          {line.id === journalEntryLines.length ? <i className="fa fa-square-plus" onClick={addNewLine}></i> :
                            <i className="fa fa-trash-can" onClick={() => removeLine(index)}></i>
                          }
                        </td>
                      </tr>
                    ))
                  }
                  <tr>
                    <td colSpan={2}>
                    </td>
                    <td>
                      {totalDebitAmount}
                    </td>
                    <td>
                      {totalCreditAmount}
                    </td>
                    <td colSpan={2}>
                    </td>
                  </tr>
                  </tbody>
                </Table>
              </div>
              <div>
                <label htmlFor="note">{t('note')}</label>
                <textarea
                  className="form-control"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  id="note"
                  rows="3">
                </textarea>
              </div>
            </form>
          </ModalBody>
          <ModalFooter>
            <button type="button" className="btn btn-success" onClick={()=>handleAddJournalEntry()}> {!!isEdit ? t("update") : t("save")} </button>
            <Button color="secondary" onClick={toggle}>
              Cancel
            </Button>
          </ModalFooter>
        </Modal>
      </>
    </>
  )
}
